import { useState } from "react";
import { App, Button, Card, Col, Form, Input, Modal, Row, Select, Space, Tabs, Tag, Typography, Upload } from "antd";
import { UploadOutlined } from "@ant-design/icons";
import { ref, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";
import { storage } from "../lib/firebase";

interface VendorItem {
  id: string;
  profile?: string;
  companyName?: string;
  url?: string;
  primaryPOC?: string;
  primaryPOCEmail?: string;
  capabilityTags?: string[];
  agreementSignedAt?: string;
  agreementRenewalDue?: string;
  agreementFileUrl?: string;
  agreementFileName?: string;
  status?: string;
  sessionId?: string;
  createdAt?: string;
}

interface VendorDetailsModalProps {
  vendor: VendorItem;
  onClose: () => void;
  onUpdateVendor: (updates: Partial<VendorItem>) => Promise<void>;
  onDeleteVendor: () => Promise<void>;
  /** Default true. When false, edit/save/upload UI is hidden. */
  canEdit?: boolean;
  /** Default true. When false, the Delete Vendor button is hidden. */
  canDelete?: boolean;
}

type TabType = "details" | "agreement";

const statusOptions = ["pending", "active", "inactive", "expired"];

export default function VendorDetailsModal({
  vendor,
  onClose,
  onUpdateVendor,
  onDeleteVendor,
  canEdit = true,
  canDelete = true,
}: VendorDetailsModalProps) {
  const { message, modal } = App.useApp();
  const ro = !canEdit;
  const [activeTab, setActiveTab] = useState<TabType>("details");
  const [editedVendor, setEditedVendor] = useState<Partial<VendorItem>>({
    profile: vendor.profile || "",
    companyName: vendor.companyName || vendor.profile?.split("\n")[0] || "",
    url: vendor.url || "",
    primaryPOC: vendor.primaryPOC || "",
    primaryPOCEmail: vendor.primaryPOCEmail || "",
    status: vendor.status || "pending",
    agreementSignedAt: vendor.agreementSignedAt,
    agreementRenewalDue: vendor.agreementRenewalDue,
    capabilityTags: vendor.capabilityTags || [],
  });
  const [uploadingAgreement, setUploadingAgreement] = useState(false);
  const [newTag, setNewTag] = useState("");

  const handleSave = async () => {
    await onUpdateVendor(editedVendor);
    onClose();
  };

  const handleDelete = async () => {
    modal.confirm({
      title: "Delete vendor",
      content: "Are you sure you want to delete this vendor?",
      okText: "Delete",
      okType: "danger",
      onOk: async () => {
        if (vendor.agreementFileUrl) {
          try {
            const fileRef = ref(storage, `vendors/${vendor.id}/agreement/${vendor.agreementFileName}`);
            await deleteObject(fileRef);
          } catch (error) {
            console.error("Error deleting agreement file:", error);
          }
        }
        await onDeleteVendor();
        onClose();
      },
    });
  };

  const handleFileUpload = async (file?: File) => {
    if (!file) return;

    setUploadingAgreement(true);
    try {
      // Delete old agreement file if it exists
      if (vendor.agreementFileUrl) {
        try {
          const oldFileRef = ref(storage, `vendors/${vendor.id}/agreement/${vendor.agreementFileName}`);
          await deleteObject(oldFileRef);
        } catch (error) {
          console.error("Error deleting old agreement file:", error);
        }
      }

      // Upload new file
      const fileRef = ref(storage, `vendors/${vendor.id}/agreement/${file.name}`);
      await uploadBytes(fileRef, file);
      const downloadURL = await getDownloadURL(fileRef);

      // Update vendor with file info and set agreementSignedAt if not already set
      const updates: Partial<VendorItem> = {
        agreementFileUrl: downloadURL,
        agreementFileName: file.name,
      };

      // If this is the first time uploading agreement, set the signed date
      if (!vendor.agreementSignedAt) {
        const now = new Date().toISOString();
        const renewalDate = new Date();
        renewalDate.setFullYear(renewalDate.getFullYear() + 1);
        updates.agreementSignedAt = now;
        updates.agreementRenewalDue = renewalDate.toISOString();
        updates.status = "active";
      }

      await onUpdateVendor(updates);
      setEditedVendor((prev) => ({ ...prev, ...updates }));
    } catch (error) {
      console.error("Error uploading agreement:", error);
      message.error("Failed to upload agreement file");
    } finally {
      setUploadingAgreement(false);
    }
  };

  const handleDeleteAgreement = async () => {
    modal.confirm({
      title: "Delete agreement",
      content: "Are you sure you want to delete the agreement file?",
      okText: "Delete",
      okType: "danger",
      onOk: async () => {
        try {
          if (vendor.agreementFileUrl) {
            const fileRef = ref(storage, `vendors/${vendor.id}/agreement/${vendor.agreementFileName}`);
            await deleteObject(fileRef);
          }

          await onUpdateVendor({
            agreementFileUrl: "",
            agreementFileName: "",
            agreementSignedAt: "",
            agreementRenewalDue: "",
            status: "pending",
          });

          setEditedVendor((prev) => ({
            ...prev,
            agreementFileUrl: "",
            agreementFileName: "",
            agreementSignedAt: "",
            agreementRenewalDue: "",
            status: "pending",
          }));
        } catch (error) {
          console.error("Error deleting agreement file:", error);
          message.error("Failed to delete agreement file");
        }
      },
    });
  };

  const handleAddTag = () => {
    if (!newTag.trim()) return;
    setEditedVendor((prev) => ({
      ...prev,
      capabilityTags: [...(prev.capabilityTags || []), newTag.trim()],
    }));
    setNewTag("");
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setEditedVendor((prev) => ({
      ...prev,
      capabilityTags: (prev.capabilityTags || []).filter((tag) => tag !== tagToRemove),
    }));
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return "Not set";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const getRenewalStatus = () => {
    if (!vendor.agreementRenewalDue) return { text: "No agreement", color: "default" };
    const renewalDate = new Date(vendor.agreementRenewalDue);
    const now = new Date();
    const daysUntil = Math.floor((renewalDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

    if (daysUntil < 0) return { text: "Expired", color: "error" };
    if (daysUntil <= 30) return { text: `${daysUntil} days until renewal`, color: "warning" };
    return { text: `${daysUntil} days until renewal`, color: "success" };
  };

  return (
    <Modal
      title={vendor.companyName || vendor.profile?.split("\n")[0] || "Vendor Details"}
      open
      onCancel={onClose}
      footer={
        <Space style={{ width: "100%", justifyContent: "space-between" }}>
          {canDelete ? (
            <Button danger onClick={handleDelete}>
              Delete Vendor
            </Button>
          ) : (
            <span />
          )}
          <Space>
            <Button onClick={onClose}>{canEdit ? "Cancel" : "Close"}</Button>
            {canEdit && (
              <Button type="primary" onClick={handleSave}>
                Save Changes
              </Button>
            )}
          </Space>
        </Space>
      }
      width={900}
      destroyOnClose
    >
      <Space direction="vertical" size="large" style={{ width: "100%" }}>
        <Space align="center" wrap>
          <Tag>{editedVendor.status}</Tag>
          {vendor.agreementSignedAt && (
            <Typography.Text type="secondary">
              Agreement signed: {formatDate(vendor.agreementSignedAt)}
            </Typography.Text>
          )}
        </Space>

        <Tabs
          activeKey={activeTab}
          onChange={(key: string) => setActiveTab(key as TabType)}
          items={[
            {
              key: "details",
              label: "Details",
              children: (
                <Space direction="vertical" size="large" style={{ width: "100%" }}>
                  <Card>
                    <Form layout="vertical" disabled={ro}>
                      <Form.Item label="Company Name">
                        <Input
                          value={editedVendor.companyName || ""}
                          onChange={(event) =>
                            setEditedVendor({ ...editedVendor, companyName: event.target.value })
                          }
                          placeholder="Enter company name"
                        />
                      </Form.Item>
                      <Form.Item label="Company Website">
                        <Input
                          value={editedVendor.url || ""}
                          onChange={(event) => setEditedVendor({ ...editedVendor, url: event.target.value })}
                          placeholder="https://example.com"
                        />
                      </Form.Item>
                      <Row gutter={16}>
                        <Col span={12}>
                          <Form.Item label="Primary POC">
                            <Input
                              value={editedVendor.primaryPOC || ""}
                              onChange={(event) =>
                                setEditedVendor({ ...editedVendor, primaryPOC: event.target.value })
                              }
                              placeholder="Contact person name"
                            />
                          </Form.Item>
                        </Col>
                        <Col span={12}>
                          <Form.Item label="Primary POC Email">
                            <Input
                              value={editedVendor.primaryPOCEmail || ""}
                              onChange={(event) =>
                                setEditedVendor({ ...editedVendor, primaryPOCEmail: event.target.value })
                              }
                              placeholder="contact@example.com"
                            />
                          </Form.Item>
                        </Col>
                      </Row>
                      <Form.Item label="Profile Description">
                        <Input.TextArea
                          value={editedVendor.profile || ""}
                          onChange={(event) => setEditedVendor({ ...editedVendor, profile: event.target.value })}
                          rows={6}
                          placeholder="Enter vendor profile and capabilities..."
                        />
                      </Form.Item>
                      <Form.Item label="Status">
                        <Select
                          value={editedVendor.status || "pending"}
                          onChange={(value) => setEditedVendor({ ...editedVendor, status: value })}
                          options={statusOptions.map((status) => ({ value: status, label: status }))}
                        />
                      </Form.Item>
                      <Form.Item label="Capability Tags">
                        <Space direction="vertical" size="small" style={{ width: "100%" }}>
                          <Space wrap>
                            {(editedVendor.capabilityTags || []).map((tag) => (
                              <Tag
                                key={tag}
                                closable={!ro}
                                onClose={() => handleRemoveTag(tag)}
                              >
                                {tag}
                              </Tag>
                            ))}
                            {(!editedVendor.capabilityTags || editedVendor.capabilityTags.length === 0) && (
                              <Typography.Text type="secondary">No capability tags yet</Typography.Text>
                            )}
                          </Space>
                          {!ro && (
                            <Space>
                              <Input
                                value={newTag}
                                onChange={(event) => setNewTag(event.target.value)}
                                placeholder="Add a tag"
                                onPressEnter={handleAddTag}
                              />
                              <Button onClick={handleAddTag}>Add Tag</Button>
                            </Space>
                          )}
                        </Space>
                      </Form.Item>
                    </Form>
                  </Card>
                </Space>
              ),
            },
            {
              key: "agreement",
              label: "Agreement",
              children: (
                <Space direction="vertical" size="large" style={{ width: "100%" }}>
                  <Card title="Vendor Agreement File">
                    {vendor.agreementFileUrl ? (
                      <Space direction="vertical" size="small" style={{ width: "100%" }}>
                        <Typography.Text>{vendor.agreementFileName}</Typography.Text>
                        <Typography.Text type="secondary">
                          Uploaded: {formatDate(vendor.agreementSignedAt)}
                        </Typography.Text>
                        <Space>
                          <Button href={vendor.agreementFileUrl} target="_blank">
                            View
                          </Button>
                          {canEdit && (
                            <Button danger onClick={handleDeleteAgreement}>
                              Delete
                            </Button>
                          )}
                        </Space>
                      </Space>
                    ) : canEdit ? (
                      <Upload
                        showUploadList={false}
                        beforeUpload={(file) => {
                          void handleFileUpload(file);
                          return false;
                        }}
                        accept=".pdf,.doc,.docx"
                      >
                        <Button icon={<UploadOutlined />} loading={uploadingAgreement}>
                          Upload Agreement
                        </Button>
                      </Upload>
                    ) : (
                      <Typography.Text type="secondary">No agreement uploaded.</Typography.Text>
                    )}
                  </Card>

                  {vendor.agreementSignedAt && (
                    <Row gutter={[16, 16]}>
                      <Col span={12}>
                        <Card>
                          <Typography.Text type="secondary">Agreement Signed</Typography.Text>
                          <Typography.Title level={5} style={{ marginTop: 8 }}>
                            {formatDate(vendor.agreementSignedAt)}
                          </Typography.Title>
                        </Card>
                      </Col>
                      <Col span={12}>
                        <Card>
                          <Typography.Text type="secondary">Renewal Due</Typography.Text>
                          <Typography.Title level={5} style={{ marginTop: 8 }}>
                            {formatDate(vendor.agreementRenewalDue)}
                          </Typography.Title>
                          <Tag color={getRenewalStatus().color}>{getRenewalStatus().text}</Tag>
                        </Card>
                      </Col>
                    </Row>
                  )}

                  <Card>
                    <Typography.Text strong>About Vendor Agreements</Typography.Text>
                    <Typography.Paragraph type="secondary">
                      Vendors can be added to the system without a signed agreement. Upload the signed agreement
                      document when available. The system will automatically set the agreement signed date and
                      calculate the renewal date (1 year from signing).
                    </Typography.Paragraph>
                  </Card>
                </Space>
              ),
            },
          ]}
        />
      </Space>
    </Modal>
  );
}
